// src/services/payrollRunStore.js
//
// Storage layer for payroll runs (PRD_PayrollRunStorage). A run's per-employee
// lines live in a subcollection
// `companies/{cid}/payrollRuns/{runId}/employees/{empId}` (the full object incl.
// frozen timeRecords), while run-level fields + denormalized `summary`
// aggregates live on the parent `payrollRuns/{runId}`, tagged with a
// `storageShape: 'subcollection'` marker (C1). This keeps every document well
// under Firestore's 1 MiB limit so runs scale to 500+ employees.
//
// Historical runs (pre-this-change) keep their inline `employees` array on the
// parent and load through the same helper via the marker — no migration (C3).
//
// This module owns the run-storage read/write/delete mechanism:
//   - loadRunEmployees   — read (marker-based shape resolution)
//   - saveRunEmployees   — write + reconcile (added in Step 2)
//   - deleteRunEmployees — recursive cleanup (added in Step 3)

import { db } from '../firebase/config';
import { doc, getDoc, setDoc, collection, getDocs, writeBatch, deleteField } from 'firebase/firestore';
import { calculatePayrollSummary } from '../utils/payrollCalculations';

// Parent marker value (C1) signalling the per-employee subcollection shape.
export const STORAGE_SHAPE_SUBCOLLECTION = 'subcollection';

/**
 * A run's summary, shape-agnostic: prefer the denormalized parent aggregates
 * (C2), else compute from the inline employees for historical runs that have no
 * parent summary. Single source for every collapsed-row / footer / dashboard
 * reader so the fallback lives in one place.
 */
export function getRunSummary(run) {
  return run?.summary || calculatePayrollSummary(run?.employees || []);
}

/** A run's employee count: parent aggregate first, inline-length fallback, else 0. */
export function runEmployeeCount(run) {
  return run?.summary?.totalEmployees ?? run?.employees?.length ?? 0;
}

/**
 * Load a payroll run's per-employee lines, resolving the storage shape from the
 * parent's `storageShape` marker (C3):
 *   - 'subcollection' → getDocs the `employees` subcollection
 *   - else (historical) → the parent's inline `employees` array
 *
 * The marker — not subcollection emptiness — is the signal, so a zero-employee
 * draft or a mid-write run resolves unambiguously.
 *
 * @param {string} companyId
 * @param {string} runId
 * @param {object|null} [run] - already-fetched parent run data; pass it to skip a
 *   redundant parent read (callers rendering a run row already have it).
 * @returns {Promise<Array>} the employee objects (with frozen timeRecords), or []
 *   if the run doc is missing.
 */
export async function loadRunEmployees(companyId, runId, run = null) {
  if (!companyId || !runId) {
    throw new Error('loadRunEmployees requires companyId and runId');
  }

  let parent = run;
  if (!parent) {
    const snap = await getDoc(doc(db, 'companies', companyId, 'payrollRuns', runId));
    if (!snap.exists()) return [];
    parent = snap.data();
  }

  if (parent.storageShape === STORAGE_SHAPE_SUBCOLLECTION) {
    const subSnap = await getDocs(
      collection(db, 'companies', companyId, 'payrollRuns', runId, 'employees'),
    );
    return subSnap.docs.map((d) => d.data());
  }

  // Historical inline run — lines still live on the parent.
  return parent.employees || [];
}

// Children-write chunking: stay under Firestore's per-commit limits (≈10 MiB
// request size, 500 ops). At ~30 KB/employee a ~4 MiB byte budget caps a chunk
// well before either limit; the op cap is a hard backstop. (Confirm the budget
// against the live limit during the seed run — PRD open item.)
const MAX_BATCH_BYTES = 4 * 1024 * 1024;
const MAX_BATCH_OPS = 450;

// Cheap O(1) per-doc size estimate. Avoids JSON.stringify-ing the whole run on
// every save (a real main-thread cost at 500 employees). timeRecords dominate
// the doc size; ~800 B/record + a ~2 KB base is a conservative upper bound, used
// only to chunk writes under Firestore's ~10 MiB commit limit. impl-review #2.
function estimateBytes(emp) {
  const records = Array.isArray(emp?.timeRecords) ? emp.timeRecords.length : 0;
  return 2 * 1024 + records * 800;
}

/**
 * Persist a run's selected employees into the subcollection and the run-level
 * fields onto the parent, per the C4 write / ordering / reconcile contract:
 *   1. upsert every selected employee sub-doc UNCONDITIONALLY (Step-4/5 edits
 *      change per-employee data even when the selection is unchanged);
 *   2. delete sub-docs for deselected employees (previousEmployeeIds − current);
 *   3. write children FIRST (chunked writeBatch, byte-budgeted), parent LAST —
 *      so a partial failure leaves a recoverable parent the caller keeps as
 *      `draft` (multi-chunk child writes are non-atomic, so ordering is
 *      mandatory, not optional);
 *   4. on the parent write, flip the `storageShape` marker and strip the inline
 *      `employees` array so a converted run never holds both shapes.
 *
 * The parent write is a full overwrite (matching the wizard's existing setDoc),
 * so omitting `employees` removes it — deleteField() is unnecessary here (and is
 * invalid in a non-merge setDoc).
 *
 * @param {string} companyId
 * @param {string} runId
 * @param {object} opts
 * @param {Array}  opts.employees - selected employee objects (full) to upsert
 * @param {Array<string>} [opts.previousEmployeeIds] - IDs persisted by the prior
 *        save, for the delete diff; pass [] on first save
 * @param {object} opts.parentData - run-level fields to write on the parent
 *        (status, summary, selectedEmployeeIds, audit, …; older runs may also carry dtrSnapshot); this
 *        helper adds `storageShape` and strips any `employees`.
 */
export async function saveRunEmployees(
  companyId,
  runId,
  { employees = [], previousEmployeeIds = [], parentData },
) {
  if (!companyId || !runId) throw new Error('saveRunEmployees requires companyId and runId');
  if (!parentData) throw new Error('saveRunEmployees requires parentData');

  const keyOf = (e) => String(e.employeeId || e.id);
  const currentIds = new Set(employees.map(keyOf));
  const toDelete = (previousEmployeeIds || []).map(String).filter((id) => !currentIds.has(id));

  const runRef = doc(db, 'companies', companyId, 'payrollRuns', runId);
  const empCol = collection(db, 'companies', companyId, 'payrollRuns', runId, 'employees');

  // 1 + 2. Reconcile children FIRST, chunked by a byte budget.
  let batch = writeBatch(db);
  let ops = 0;
  let bytes = 0;
  let __batchNum = 0; // SPIKE (OBS-1): remove after root-cause measurement
  let __totalOps = 0; // SPIKE (OBS-1)
  const flush = async () => {
    if (ops > 0) {
      // SPIKE (OBS-1): time each commit + record op/byte counts so the console shows
      // whether resource-exhausted is per-commit (size/op cap) or cumulative (queue depth).
      const __n = ops;
      const __kb = Math.round(bytes / 1024);
      const __t = Date.now();
      try {
        await batch.commit();
      } catch (e) {
        console.error(`[save-perf] batch #${__batchNum + 1} commit FAILED: ${__n} ops, ~${__kb}KB, ${__totalOps} ops written before this:`, e.code || e.message); // SPIKE (OBS-1)
        throw e;
      }
      __batchNum += 1;
      __totalOps += __n;
      console.log(`[save-perf] batch #${__batchNum} committed: ${__n} ops, ~${__kb}KB in ${Date.now() - __t}ms (cumulative ${__totalOps} ops)`); // SPIKE (OBS-1)
      batch = writeBatch(db);
      ops = 0;
      bytes = 0;
    }
  };
  for (const emp of employees) {
    const size = estimateBytes(emp);
    if (ops >= MAX_BATCH_OPS || (ops > 0 && bytes + size > MAX_BATCH_BYTES)) {
      await flush();
    }
    batch.set(doc(empCol, keyOf(emp)), emp);
    ops += 1;
    bytes += size;
  }
  for (const id of toDelete) {
    if (ops >= MAX_BATCH_OPS) await flush();
    batch.delete(doc(empCol, id));
    ops += 1;
  }
  await flush();
  console.log(`[save-perf] saveRunEmployees children done: ${employees.length} upserts + ${toDelete.length} deletes → ${__batchNum} batches, ${__totalOps} ops`); // SPIKE (OBS-1)

  // 3 + 4. Parent LAST — strip the inline `employees` array and flip the marker.
  const { employees: _omitInline, ...parentRest } = parentData;
  await setDoc(runRef, {
    ...parentRest,
    storageShape: STORAGE_SHAPE_SUBCOLLECTION,
  });
}

/**
 * Write ONLY the parent doc (PRD_PayrollSavePerf C1). The per-employee children write
 * now goes server-side via the `savePayrollRun` callable; the client writes the parent
 * AFTER the callable returns (children-first/parent-last). Same parent contract as
 * `saveRunEmployees`' last step — strip the inline `employees` array + flip the
 * `storageShape` marker so a converted run never holds both shapes (PRD_PayrollRunStorage C4).
 *
 * NOTE (PRD_StandaloneAdjustments C19, impl-review #13): `excludedAdjustmentIds`
 * persists through here via the `parentData` spread — nothing names it, only
 * `employees` is stripped. If this helper ever moves to an allow-list of parent
 * fields, that field must be on it or Remove-from-run silently stops sticking
 * across resume.
 *
 * @param {string} companyId
 * @param {string} runId
 * @param {object} parentData - run-level fields (status, summary, selectedEmployeeIds,
 *        audit, …; older runs may also carry dtrSnapshot); this helper strips any `employees` + adds `storageShape`.
 */
export async function saveRunParent(companyId, runId, parentData) {
  if (!companyId || !runId) throw new Error('saveRunParent requires companyId and runId');
  if (!parentData) throw new Error('saveRunParent requires parentData');
  const runRef = doc(db, 'companies', companyId, 'payrollRuns', runId);
  const { employees: _omitInline, ...parentRest } = parentData;
  // MERGE (not full overwrite) so the run-creation reservation's `createdAt`/`createdBy`
  // (which the draft save's `parentData` doesn't carry — isDraft is already true post-
  // reservation, S5) are PRESERVED rather than wiped (impl-review #2). Explicitly strip any
  // inline `employees` array via deleteField() (valid under merge) to keep the
  // PRD_PayrollRunStorage C4 invariant — a converted run never holds both shapes.
  await setDoc(runRef, {
    ...parentRest,
    storageShape: STORAGE_SHAPE_SUBCOLLECTION,
    employees: deleteField(),
  }, { merge: true });
}

/**
 * Recursively delete a run's `employees` subcollection (C7). Firestore does not
 * cascade-delete subcollections, so the caller must clear it BEFORE deleting the
 * parent — else orphaned `employees/{empId}` docs accumulate and, on a runId
 * reuse, would make loadRunEmployees return stale lines. Chunked at the 500-op
 * batch cap (byte budget is irrelevant for deletes). No-op (returns 0) for a
 * historical inline run, whose subcollection is empty.
 *
 * @returns {Promise<number>} the number of sub-docs deleted
 */
export async function deleteRunEmployees(companyId, runId) {
  if (!companyId || !runId) throw new Error('deleteRunEmployees requires companyId and runId');

  const empCol = collection(db, 'companies', companyId, 'payrollRuns', runId, 'employees');
  const snap = await getDocs(empCol);
  if (snap.empty) return 0;

  let batch = writeBatch(db);
  let ops = 0;
  for (const d of snap.docs) {
    batch.delete(d.ref);
    ops += 1;
    if (ops >= MAX_BATCH_OPS) {
      await batch.commit();
      batch = writeBatch(db);
      ops = 0;
    }
  }
  if (ops > 0) await batch.commit();
  return snap.size;
}
